import React, { useState, useEffect } from 'react';
import { Cloud, Sun, CloudRain, CloudSnow, CloudDrizzle, Wind, Droplets, Thermometer } from 'lucide-react';

const GOULDSBORO_LAT = 44.48;
const GOULDSBORO_LON = -68.04;

const weatherCodeMap = {
  0: { label: 'Clear Sky', icon: Sun },
  1: { label: 'Mostly Clear', icon: Sun },
  2: { label: 'Partly Cloudy', icon: Cloud },
  3: { label: 'Overcast', icon: Cloud },
  45: { label: 'Foggy', icon: Cloud },
  48: { label: 'Rime Fog', icon: Cloud },
  51: { label: 'Light Drizzle', icon: CloudDrizzle },
  53: { label: 'Drizzle', icon: CloudDrizzle },
  55: { label: 'Heavy Drizzle', icon: CloudDrizzle },
  61: { label: 'Light Rain', icon: CloudRain },
  63: { label: 'Rain', icon: CloudRain },
  65: { label: 'Heavy Rain', icon: CloudRain },
  71: { label: 'Light Snow', icon: CloudSnow },
  73: { label: 'Snow', icon: CloudSnow },
  75: { label: 'Heavy Snow', icon: CloudSnow },
  80: { label: 'Rain Showers', icon: CloudRain },
  81: { label: 'Rain Showers', icon: CloudRain },
  82: { label: 'Heavy Showers', icon: CloudRain },
  95: { label: 'Thunderstorm', icon: CloudRain },
};

const getWeatherInfo = (code) => weatherCodeMap[code] || { label: 'Unknown', icon: Cloud };

const celsiusToFahrenheit = (c) => Math.round((c * 9) / 5 + 32);

const WeatherWidget = () => {
  const [weather, setWeather] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchWeather = async () => {
      try {
        const res = await fetch(
          `https://api.open-meteo.com/v1/forecast?latitude=${GOULDSBORO_LAT}&longitude=${GOULDSBORO_LON}&current=temperature_2m,weathercode,windspeed_10m,relative_humidity_2m&daily=weathercode,temperature_2m_max,temperature_2m_min&temperature_unit=celsius&windspeed_unit=mph&timezone=America/New_York&forecast_days=5`
        );
        const data = await res.json();
        setWeather(data);
      } catch (err) {
        console.error('Weather fetch failed:', err);
      } finally {
        setLoading(false);
      }
    };
    fetchWeather();
  }, []);

  if (loading || !weather) {
    return (
      <section className="section-spacing" data-testid="weather-widget">
        <div className="container-coastal text-center">
          <h2 className="section-title">Local Weather</h2>
          <p style={{ color: 'var(--color-granite-gray)' }}>Loading forecast...</p>
        </div>
      </section>
    );
  }

  const current = weather.current;
  const daily = weather.daily;
  const currentInfo = getWeatherInfo(current.weathercode);
  const CurrentIcon = currentInfo.icon;
  const dayNames = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

  return (
    <section className="section-spacing" data-testid="weather-widget" style={{ background: 'var(--color-ocean-deep)', color: 'white' }}>
      <div className="container-coastal">
        <div className="text-center mb-xl">
          <h2 className="section-title" style={{ color: 'white' }}>Schoodic Peninsula Weather</h2>
          <p style={{ color: 'rgba(255,255,255,0.7)', maxWidth: '600px', margin: '0 auto' }}>
            Current conditions in Gouldsboro, Maine
          </p>
        </div>

        {/* Current Weather */}
        <div style={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          gap: '3rem',
          flexWrap: 'wrap',
          marginBottom: 'var(--spacing-2xl)'
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '1.5rem' }}>
            <CurrentIcon size={64} strokeWidth={1.5} />
            <div>
              <div style={{ fontSize: '3.5rem', fontFamily: 'var(--font-display)', fontWeight: '700', lineHeight: 1 }}>
                {celsiusToFahrenheit(current.temperature_2m)}&deg;F
              </div>
              <div style={{ fontSize: '1.125rem', opacity: 0.8 }}>{currentInfo.label}</div>
            </div>
          </div>
          <div style={{ display: 'flex', gap: '2rem', flexWrap: 'wrap' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', opacity: 0.8 }}>
              <Wind size={20} />
              <span>{Math.round(current.windspeed_10m)} mph</span>
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', opacity: 0.8 }}>
              <Droplets size={20} />
              <span>{current.relative_humidity_2m}%</span>
            </div>
          </div>
        </div>

        {/* 5-Day Forecast */}
        <div style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(5, 1fr)',
          gap: 'var(--spacing-md)',
          maxWidth: '800px',
          margin: '0 auto'
        }}>
          {daily.time.map((date, i) => {
            const d = new Date(date + 'T12:00:00');
            const dayName = i === 0 ? 'Today' : dayNames[d.getDay()];
            const info = getWeatherInfo(daily.weathercode[i]);
            const DayIcon = info.icon;
            return (
              <div key={date} data-testid={`forecast-day-${i}`} style={{
                textAlign: 'center',
                padding: 'var(--spacing-md)',
                background: 'rgba(255,255,255,0.1)',
                borderRadius: 'var(--border-radius-lg)',
                backdropFilter: 'blur(10px)'
              }}>
                <div style={{ fontWeight: '600', marginBottom: '0.5rem', fontSize: '0.9rem' }}>{dayName}</div>
                <DayIcon size={28} style={{ margin: '0.5rem auto' }} />
                <div style={{ fontSize: '0.75rem', opacity: 0.7, marginBottom: '0.5rem' }}>{info.label}</div>
                <div style={{ display: 'flex', justifyContent: 'center', gap: '0.5rem', fontSize: '0.9rem' }}>
                  <span style={{ fontWeight: '700' }}>{celsiusToFahrenheit(daily.temperature_2m_max[i])}&deg;</span>
                  <span style={{ opacity: 0.6 }}>{celsiusToFahrenheit(daily.temperature_2m_min[i])}&deg;</span>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
};

export default WeatherWidget;
